const toText = (value: unknown) => String(value ?? "").trim();

function colorLuminance(color: string) {
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i)?.[1];
  if (!hex) return null;
  const normalized = hex.length === 3
    ? hex.split("").map((digit) => `${digit}${digit}`).join("")
    : hex.slice(0, 6);
  const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16));
  const linear = (channel: number) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(channels[0]) + 0.7152 * linear(channels[1]) + 0.0722 * linear(channels[2]);
}

function readableTextColor(background: string, fallback = "#ffffff") {
  const luminance = colorLuminance(background);
  if (luminance === null) return fallback;
  const whiteContrast = 1.05 / (luminance + 0.05);
  const darkLuminance = 0.005605391624202723;
  const darkContrast = (luminance + 0.05) / (darkLuminance + 0.05);
  return darkContrast >= whiteContrast ? "#111111" : "#ffffff";
}

function accessibleTextColor(background: string, requested: string, fallback = "#ffffff") {
  if (!requested) return readableTextColor(background, fallback);
  const backgroundLuminance = colorLuminance(background);
  const textLuminance = colorLuminance(requested);
  if (backgroundLuminance === null || textLuminance === null) return requested;
  const contrast = (Math.max(backgroundLuminance, textLuminance) + 0.05)
    / (Math.min(backgroundLuminance, textLuminance) + 0.05);
  return contrast >= 4.5 ? requested : readableTextColor(background, fallback);
}

type TerminalTheme = {
  background?: string | null;
  buttonColor?: string | null;
  buttonTextColor?: string | null;
  secondaryButtonColor?: string | null;
  secondaryButtonTextColor?: string | null;
  addToCartButtonColor?: string | null;
  addToCartButtonTextColor?: string | null;
  categoryButtonColor?: string | null;
  categoryButtonTextColor?: string | null;
  categoryButtonActiveColor?: string | null;
  categoryButtonActiveTextColor?: string | null;
  cartBarColor?: string | null;
  cartBarTextColor?: string | null;
  cartBarHighlightColor?: string | null;
  cartBarHighlightTextColor?: string | null;
  productPriceBadgeColor?: string | null;
  productPriceBadgeTextColor?: string | null;
};

type TerminalTypography = {
  bodyFontFamily?: string | null;
  bodyFontCssUrl?: string | null;
  displayFontFamily?: string | null;
  displayFontCssUrl?: string | null;
};

type TerminalConsumerRuntimeConfig = {
  theme?: TerminalTheme | null;
  layout?: {
    productGridColumns?: number | null;
    categoryGridColumns?: number | null;
    categoryTileImageSource?: "category" | "first-product" | null;
  } | null;
  idleScreen?: {
    logoUrl?: string | null;
    logoPosition?:
      | "top-left"
      | "top-center"
      | "top-right"
      | "center-left"
      | "center"
      | "center-right"
      | "bottom-left"
      | "bottom-center"
      | "bottom-right"
      | null;
    logoWidth?: number | null;
    overlayOpacity?: number | null;
  } | null;
  typography?: TerminalTypography | null;
  localization?: {
    availableLanguages?: string[] | null;
    defaultLanguage?: string | null;
  } | null;
};

type TerminalConfig = {
  theme?: TerminalTheme | null;
  consumerRuntime?: TerminalConsumerRuntimeConfig | null;
};

export function readTerminalConfig(): TerminalConfig {
  try {
    const raw = localStorage.getItem("terminalConfig");
    if (!raw) return {};
    const parsed = JSON.parse(raw) as TerminalConfig;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function resolveTheme(config?: TerminalConfig | null, theme?: TerminalTheme | null) {
  const rootTheme =
    config?.theme && typeof config.theme === "object"
      ? config.theme
      : {};
  const consumerRuntimeTheme =
    config?.consumerRuntime?.theme && typeof config.consumerRuntime.theme === "object"
      ? config.consumerRuntime.theme
      : {};
  const explicitTheme =
    theme && typeof theme === "object"
      ? theme
      : {};

  return {
    ...rootTheme,
    ...consumerRuntimeTheme,
    ...explicitTheme,
  };
}

function resolveColumns(config?: TerminalConfig | null) {
  const value = Number(config?.consumerRuntime?.layout?.productGridColumns ?? 5);
  if (!Number.isFinite(value)) return 5;
  return Math.max(2, Math.min(6, Math.round(value)));
}

function resolveCategoryColumns(config?: TerminalConfig | null) {
  const value = Number(config?.consumerRuntime?.layout?.categoryGridColumns ?? 3);
  if (!Number.isFinite(value)) return 3;
  return Math.max(1, Math.min(6, Math.round(value)));
}

function resolveTypography(config?: TerminalConfig | null) {
  const source = config?.consumerRuntime?.typography;
  return source && typeof source === "object" ? source : {};
}

function syncFontStylesheet(id: string, href: string) {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  if (!href) {
    existing?.remove();
    return;
  }
  const nextHref = href.trim();
  if (!nextHref) {
    existing?.remove();
    return;
  }
  if (existing) {
    if (existing.href !== nextHref) existing.href = nextHref;
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = nextHref;
  document.head.appendChild(link);
}

const waitForStylesheet = (link: HTMLLinkElement | null): Promise<void> => {
  if (!link || link.sheet) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => resolve();
    link.addEventListener("load", done, { once: true });
    link.addEventListener("error", done, { once: true });
  });
};

const withTimeout = (promise: Promise<unknown>, timeoutMs: number): Promise<void> =>
  Promise.race([
    promise.catch(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, timeoutMs)),
  ]).then(() => undefined);

export async function waitForTerminalThemeAssets(
  configArg?: TerminalConfig | null,
  timeoutMs = 5_000,
): Promise<void> {
  if (typeof document === "undefined") return;
  const config = configArg && typeof configArg === "object" ? configArg : readTerminalConfig();
  const typography = resolveTypography(config);
  const bodyFontFamily = toText(typography.bodyFontFamily) || "'Oswald', sans-serif";
  const displayFontFamily = toText(typography.displayFontFamily) || bodyFontFamily;
  const stylesheets = Promise.all([
    waitForStylesheet(document.getElementById("dq-font-body-css") as HTMLLinkElement | null),
    waitForStylesheet(document.getElementById("dq-font-display-css") as HTMLLinkElement | null),
  ]);

  await withTimeout(stylesheets, timeoutMs);
  if (!document.fonts) return;
  await withTimeout(
    Promise.all([
      document.fonts.load(`400 16px ${bodyFontFamily}`),
      document.fonts.load(`600 24px ${displayFontFamily}`),
      document.fonts.ready,
    ]),
    timeoutMs,
  );
}

export function applyTerminalTheme(theme?: TerminalTheme | null, configArg?: TerminalConfig | null) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const storedConfig = configArg && typeof configArg === "object" ? configArg : readTerminalConfig();
  const current = resolveTheme(storedConfig, theme);
  const buttonColor = toText(current.buttonColor) || "#f59e0b";
  const buttonTextColor = accessibleTextColor(buttonColor, toText(current.buttonTextColor));
  const secondaryButtonColor = toText(current.secondaryButtonColor) || "#555555";
  const secondaryButtonTextColor = accessibleTextColor(secondaryButtonColor, toText(current.secondaryButtonTextColor));
  const addToCartButtonColor = toText(current.addToCartButtonColor) || "#0f766e";
  const addToCartButtonTextColor = accessibleTextColor(addToCartButtonColor, toText(current.addToCartButtonTextColor));
  const categoryButtonColor = toText(current.categoryButtonColor) || "#333333";
  const categoryButtonTextColor = accessibleTextColor(categoryButtonColor, toText(current.categoryButtonTextColor));
  const categoryButtonActiveColor = toText(current.categoryButtonActiveColor) || buttonColor;
  const categoryButtonActiveTextColor =
    accessibleTextColor(categoryButtonActiveColor, toText(current.categoryButtonActiveTextColor));
  const cartBarColor = toText(current.cartBarColor) || "#1a1a1a";
  const cartBarTextColor = accessibleTextColor(cartBarColor, toText(current.cartBarTextColor));
  const cartBarHighlightColor = toText(current.cartBarHighlightColor) || "#ff7b00";
  const cartBarHighlightTextColor =
    accessibleTextColor(cartBarHighlightColor, toText(current.cartBarHighlightTextColor));
  const productPriceBadgeColor = toText(current.productPriceBadgeColor) || "#2f2f2f";
  const productPriceBadgeTextColor = accessibleTextColor(productPriceBadgeColor, toText(current.productPriceBadgeTextColor));
  const background = toText(current.background) || "#111827";
  const productGridColumns = resolveColumns(storedConfig);
  const categoryGridColumns = resolveCategoryColumns(storedConfig);
  const typography = resolveTypography(storedConfig);
  const bodyFontFamily = toText(typography.bodyFontFamily) || "'Oswald', sans-serif";
  const bodyFontCssUrl =
    toText(typography.bodyFontCssUrl) ||
    "https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&display=swap";
  const displayFontFamily = toText(typography.displayFontFamily) || bodyFontFamily;
  const displayFontCssUrl = toText(typography.displayFontCssUrl) || bodyFontCssUrl;

  root.style.setProperty("--dq-accent", buttonColor);
  root.style.setProperty("--dq-button-bg", buttonColor);
  root.style.setProperty("--dq-button-text", buttonTextColor);
  root.style.setProperty("--dq-button-bg-alt", secondaryButtonColor);
  root.style.setProperty("--dq-button-text-alt", secondaryButtonTextColor);
  root.style.setProperty("--dq-button-bg-cart", addToCartButtonColor);
  root.style.setProperty("--dq-button-text-cart", addToCartButtonTextColor);
  root.style.setProperty("--dq-category-button-bg", categoryButtonColor);
  root.style.setProperty("--dq-category-button-text", categoryButtonTextColor);
  root.style.setProperty("--dq-category-button-bg-active", categoryButtonActiveColor);
  root.style.setProperty("--dq-category-button-text-active", categoryButtonActiveTextColor);
  root.style.setProperty("--dq-cart-bar-bg", cartBarColor);
  root.style.setProperty("--dq-cart-bar-text", cartBarTextColor);
  root.style.setProperty("--dq-cart-bar-bg-highlight", cartBarHighlightColor);
  root.style.setProperty("--dq-cart-bar-text-highlight", cartBarHighlightTextColor);
  root.style.setProperty("--dq-product-price-badge-bg", productPriceBadgeColor);
  root.style.setProperty("--dq-product-price-badge-text", productPriceBadgeTextColor);
  root.style.setProperty("--dq-terminal-background", background);
  root.style.setProperty("--dq-product-columns", String(productGridColumns));
  root.style.setProperty("--dq-category-columns", String(categoryGridColumns));
  root.style.setProperty("--dq-font-body", bodyFontFamily);
  root.style.setProperty("--dq-font-display", displayFontFamily);

  syncFontStylesheet("dq-font-body-css", bodyFontCssUrl);
  syncFontStylesheet("dq-font-display-css", displayFontCssUrl);
}

export function persistAndApplyTerminalTheme(config?: TerminalConfig | null) {
  applyTerminalTheme(config?.theme || null, config || null);
}
